import routeAccess from './access'
import policy from './policy'
import sessionstorage from '@/components/cache'
import baseUrl from '@/assets/js/baseUrl'

const DEFAULT_LOGIN_RETURN_PATH = '/home/index'
const loadingOwners = []
let loadingElement = null
let latestNavId = ''
let navigationSequence = 0
let loginRedirectPromise = null
let routerInstance = null
let titleCandidate = null

function nextNavId() {
  navigationSequence += 1
  return String(Date.now()) + '-' + String(navigationSequence)
}

function showGuardLoading(owner) {
  if (loadingOwners.indexOf(owner) === -1) loadingOwners.push(owner)
  if (loadingElement || !document.body) return
  loadingElement = document.createElement('div')
  loadingElement.setAttribute('role', 'status')
  loadingElement.setAttribute('aria-live', 'polite')
  loadingElement.textContent = '正在校验登录状态…'
  loadingElement.style.cssText = 'position:fixed;inset:0;z-index:9999;display:flex;align-items:center;justify-content:center;background:rgba(255,255,255,.92);color:#333;font-size:16px;'
  document.body.appendChild(loadingElement)
}

function hideGuardLoading(owner) {
  const index = loadingOwners.indexOf(owner)
  if (index !== -1) loadingOwners.splice(index, 1)
  if (loadingOwners.length || !loadingElement) return
  if (loadingElement.parentNode) loadingElement.parentNode.removeChild(loadingElement)
  loadingElement = null
}

function hideAllGuardLoading() {
  loadingOwners.splice(0, loadingOwners.length)
  if (loadingElement && loadingElement.parentNode) loadingElement.parentNode.removeChild(loadingElement)
  loadingElement = null
}

function queryWhitelist() {
  // 当前机器基线没有逐页 query 白名单，登录回跳只恢复路径，不恢复业务写操作。
  return []
}

function saveIntent(to, navId) {
  const intent = policy.createPendingIntent({
    path: to && to.path,
    query: to && to.query
  }, routeAccess, queryWhitelist, navId)
  if (intent) policy.savePendingIntent(window.sessionStorage, intent)
}

function getTopDomain() {
  const domain = document.domain || window.location.hostname
  const domains = domain.split('.')
  const marks = ['com', 'cn']
  for (let m = 0; m < marks.length; m += 1) {
    for (let i = 0; i < domains.length; i += 1) {
      if (domains[i] === marks[m] && i > 0) return '.' + domain.substring(domain.indexOf(domains[i - 1]))
    }
  }
  return domain
}

function getCookie(key) {
  const match = document.cookie.match(new RegExp('(^| )' + key + '=([^;]*)(;|$)'))
  if (!match) return null
  try {
    return decodeURIComponent(match[2])
  } catch (error) {
    return null
  }
}

function getQueryString(name) {
  const match = window.location.search.substr(1).match(new RegExp('(^|&)' + name + '=([^&]*)(&|$)', 'i'))
  if (!match) return null
  try {
    return decodeURIComponent(match[2].replace(/\+/g, ' '))
  } catch (error) {
    return null
  }
}

function persistEmployeeToken(token) {
  sessionstorage.session_set('token', token)
  document.cookie = 'jtl=' + encodeURIComponent(token) + ';path=/'
  document.cookie = 'jtlf=' + encodeURIComponent('Basic ' + token) + ';path=/;domain=' + getTopDomain()
}

function getEmployeeLoginState(to) {
  const queryToken = to && to.query && policy.hasText(to.query.jtl) ? to.query.jtl : getQueryString('jtl')
  if (policy.hasText(queryToken)) {
    persistEmployeeToken(queryToken)
    return { status: 'authenticated', token: queryToken }
  }
  const cookieToken = getCookie('jtl')
  if (policy.hasText(cookieToken)) {
    persistEmployeeToken(cookieToken)
    return { status: 'authenticated', token: cookieToken }
  }
  return { status: 'anonymous', reason: 'EMPTY_TOKEN' }
}

function buildLoginUrl(target) {
  const config = {
    getst: baseUrl.getst,
    loginBack: baseUrl.loginBack,
    address: baseUrl.address
  }
  return policy.buildSsoLoginUrl(target, window.location, config, routeAccess, queryWhitelist) ||
    policy.buildSsoLoginUrl({ path: DEFAULT_LOGIN_RETURN_PATH, query: {} }, window.location, config, routeAccess, queryWhitelist)
}

export function redirectToLogin(target, targetNavId, options) {
  if (loginRedirectPromise) return loginRedirectPromise
  if (targetNavId && targetNavId !== latestNavId) return Promise.resolve()

  const current = target || (routerInstance && routerInstance.currentRoute) || { path: DEFAULT_LOGIN_RETURN_PATH, query: {} }
  const navId = targetNavId || nextNavId()
  saveIntent(current, navId)
  const loginUrl = buildLoginUrl(current)

  if (options && options.expired) alert('登录状态失效,请重新登录')
  if (loginUrl) {
    window.location.href = loginUrl
  } else {
    alert('登录地址配置无效，请联系管理员')
  }
  // 登录页为整页跳转，在页面卸载前持续复用同一 Promise，覆盖并发401与重复导航。
  loginRedirectPromise = Promise.resolve()
  return loginRedirectPromise
}

function markTitle(to, navId) {
  titleCandidate = { to: to, navId: navId }
}

function toNotFound(next) {
  next({ name: 'NotFound', replace: true })
}

export function installRouteGuard(router, options) {
  routerInstance = router
  const settings = options || {}

  router.beforeEach((to, from, next) => {
    hideAllGuardLoading()
    const navId = nextNavId()
    latestNavId = navId
    titleCandidate = null
    const done = policy.once(next)

    if (policy.isSameNavigation(to, from)) {
      done(false)
      return
    }

    const resolved = policy.resolveAccess(to.matched, routeAccess)
    const access = resolved.access
    if (!access) {
      if (to.name === 'NotFound') done(false)
      else toNotFound(done)
      return
    }

    if (access === 'public' || access === 'business' || access === 'guest') {
      markTitle(to, navId)
      done()
      return
    }

    if (access !== 'employee') {
      if (to.name === 'NotFound') done(false)
      else toNotFound(done)
      return
    }

    showGuardLoading(navId)
    const finish = () => hideGuardLoading(navId)
    Promise.resolve().then(() => getEmployeeLoginState(to)).then(state => {
      if (latestNavId !== navId) {
        done(false)
        return
      }
      if (state.status === 'anonymous') {
        saveIntent(to, navId)
        redirectToLogin(to, navId)
        done(false)
        return
      }
      if (state.status !== 'authenticated') {
        alert('登录状态获取失败，请稍后重试')
        done(false)
        return
      }

      policy.clearPendingIntent(window.sessionStorage)
      if (typeof settings.onAuthenticated === 'function') settings.onAuthenticated(to, from)
      markTitle(to, navId)
      done()
    }).catch(() => {
      if (latestNavId === navId) {
        alert('登录状态获取失败，请稍后重试')
        done(false)
      } else {
        done(false)
      }
    }).then(finish, finish)
  })

  router.afterEach(to => {
    if (!titleCandidate || titleCandidate.to !== to || titleCandidate.navId !== latestNavId) return
    const title = policy.resolveTitle(to.matched)
    if (title) document.title = title
    titleCandidate = null
  })
}



// WEBPACK FOOTER //
// ./src/router/guard.js